import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Pagination, Spinner, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import FeatherIcon from "feather-icons-react";

import api from "../../../../utils/ApiMethod";
import { RootState } from "../../../../redux/store";
import { logoutUser } from "../../../../redux/actions";
import { ADMIN_LOGIN_BASE_URL } from "../../../../apiservices/apiService";
import districtContactsData from "../../../../data/districtContacts.json";

export interface DistrictQuerySummaryRow {
  code: string;
  district: string;
  Open: number;
  "In-Review": number;
  Pending: number;
  Closed: number;
  total: number;
}

interface DistrictContact {
  name: string;
  contact: string;
}

type SortKey = "district" | "open" | "pending" | "closed" | "total";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "district", label: "District (A-Z)" },
  { value: "open", label: "Most open" },
  { value: "pending", label: "Most pending / in-review" },
  { value: "closed", label: "Most closed" },
  { value: "total", label: "Most total" },
];

const DISTRICTS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const pendingOf = (row: DistrictQuerySummaryRow) =>
  (row.Pending || 0) + (row["In-Review"] || 0);

// District coordinator contacts (src/data/districtContacts.json), keyed by the
// district code in each entry's "NAME (CODE)" string - the backend's summary
// rows carry the same code (utils/biharDistricts.js), which sidesteps name
// differences like "PURBA CHAMPARAN" vs the helpdesk's "East Champaran
// (Motihari)". Built once at module load, not per render.
const contactsByCode: Record<string, DistrictContact[]> = (() => {
  const map: Record<string, DistrictContact[]> = {};
  (
    districtContactsData as { name: string; contact: string; districts: string[] }[]
  ).forEach((person) => {
    person.districts.forEach((district) => {
      const code = district.match(/\((\d+)\)\s*$/)?.[1];
      if (!code) return;
      (map[code] = map[code] || []).push({ name: person.name, contact: person.contact });
    });
  });
  return map;
})();

const ContactCell: React.FC<{ code: string }> = ({ code }) => {
  const contacts = contactsByCode[code];
  if (!contacts?.length) return <span className="text-muted">-</span>;
  return (
    <>
      {contacts.map((c) => (
        <div key={c.name + c.contact} className="text-nowrap">
          <span className="fw-semibold">{c.name}</span>
          <br />
          <a
            href={`tel:${c.contact.replace(/\s+/g, "")}`}
            className="small"
            onClick={(e) => e.stopPropagation()}
          >
            {c.contact}
          </a>
        </div>
      ))}
    </>
  );
};

interface Props {
  // Applies the district (by code) to the query list below and closes the modal.
  onSelectDistrict: (code: string) => void;
  // Bumped by the parent after a query status update so an open modal refetches.
  refreshKey?: number;
}

// Fetches one page of districts at a time from the Redis-backed
// /api/query/district-summary (paged/sorted/searched server-side), and only
// while open - nothing is loaded until the button is clicked.
const DistrictQuerySummaryModal: React.FC<Props> = ({ onSelectDistrict, refreshKey = 0 }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => ({ user: state.Auth.user }));
  const profileId = user?._id ?? user?.user?._id ?? "";
  const Key_Key = user?.moniterd_ttl ?? user?.user?.moniterd_ttl ?? "";

  const [show, setShow] = useState(false);
  const [rows, setRows] = useState<DistrictQuerySummaryRow[]>([]);
  const [unspecified, setUnspecified] = useState<DistrictQuerySummaryRow | null>(null);
  const [totals, setTotals] = useState<Omit<DistrictQuerySummaryRow, "code" | "district"> | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDistricts, setTotalDistricts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("district");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchPage = useCallback(async () => {
    if (!profileId || !Key_Key) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(DISTRICTS_PER_PAGE),
        sort: sortKey,
      });
      if (search) params.set("search", search);

      const response = await api.fetchDataAuth(
        `/api/query/district-summary/${profileId}/${Key_Key}?${params.toString()}`
      );
      if (response?.status === 440) {
        alert("Session Expired");
        dispatch(logoutUser());
        window.location.href = ADMIN_LOGIN_BASE_URL;
        return;
      }
      if (response?.data?.status) {
        setRows(Array.isArray(response.data.data) ? response.data.data : []);
        setUnspecified(response.data.unspecified || null);
        setTotals(response.data.totals || null);
        setTotalPages(response.data.totalPages || 1);
        setTotalDistricts(response.data.total || 0);
      } else {
        setError(
          response?.data?.message || response?.message || "Could not load the district-wise summary."
        );
        setRows([]);
      }
    } catch (err) {
      console.error("Error fetching district summary:", err);
      setError("Could not load the district-wise summary.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [profileId, Key_Key, page, sortKey, search, dispatch]);

  useEffect(() => {
    if (show) fetchPage();
  }, [show, fetchPage, refreshKey]);

  const handleOpen = () => {
    setPage(1);
    setSearchInput("");
    setSearch("");
    setSortKey("district");
    setShow(true);
  };

  const handleSelect = (row: DistrictQuerySummaryRow) => {
    onSelectDistrict(row.code);
    setShow(false);
  };

  const rangeStart = totalDistricts === 0 ? 0 : (page - 1) * DISTRICTS_PER_PAGE + 1;
  const rangeEnd = Math.min(page * DISTRICTS_PER_PAGE, totalDistricts);

  return (
    <>
      <Button variant="outline-primary" onClick={handleOpen}>
        <FeatherIcon icon="map-pin" size={14} className="me-1" />
        District-wise Summary
      </Button>

      <Modal show={show} onHide={() => setShow(false)} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>District-wise Query Summary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Form.Control
              type="search"
              size="sm"
              placeholder="Search district..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ flex: "1 1 220px", maxWidth: 360 }}
            />
            <Form.Select
              size="sm"
              value={sortKey}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setSortKey(e.target.value as SortKey);
                setPage(1);
              }}
              className="w-auto"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  Sort: {o.label}
                </option>
              ))}
            </Form.Select>
            <Button size="sm" variant="light" onClick={fetchPage} disabled={loading} title="Refresh">
              <FeatherIcon icon="refresh-cw" size={14} />
            </Button>
          </div>

          {error ? <Alert variant="danger">{error}</Alert> : null}

          <div className="position-relative">
            {loading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ background: "rgba(255,255,255,0.6)", zIndex: 1 }}
              >
                <Spinner animation="border" size="sm" />
              </div>
            )}
            <Table hover size="sm" responsive className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>District</th>
                  <th>Contact Person</th>
                  <th className="text-end text-danger">Open</th>
                  <th className="text-end text-warning">Pending / In-Review</th>
                  <th className="text-end text-success">Closed</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-3">
                      No districts found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr
                      key={row.code}
                      onClick={() => handleSelect(row)}
                      style={{ cursor: "pointer" }}
                      title={`Show ${row.district} queries`}
                    >
                      <td className="text-muted">{rangeStart + i}</td>
                      <td className="fw-semibold">{row.district}</td>
                      <td>
                        <ContactCell code={row.code} />
                      </td>
                      <td className="text-end fw-semibold">{row.Open.toLocaleString()}</td>
                      <td className="text-end">{pendingOf(row).toLocaleString()}</td>
                      <td className="text-end">{row.Closed.toLocaleString()}</td>
                      <td className="text-end">{row.total.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {totals && (
                <tfoot>
                  {unspecified && (
                    <tr className="text-muted">
                      <td />
                      <td colSpan={2}>District not specified</td>
                      <td className="text-end">{unspecified.Open.toLocaleString()}</td>
                      <td className="text-end">{pendingOf(unspecified).toLocaleString()}</td>
                      <td className="text-end">{unspecified.Closed.toLocaleString()}</td>
                      <td className="text-end">{unspecified.total.toLocaleString()}</td>
                    </tr>
                  )}
                  <tr className="fw-bold">
                    <td />
                    <td colSpan={2}>Total (all districts)</td>
                    <td className="text-end">{totals.Open.toLocaleString()}</td>
                    <td className="text-end">
                      {((totals.Pending || 0) + (totals["In-Review"] || 0)).toLocaleString()}
                    </td>
                    <td className="text-end">{totals.Closed.toLocaleString()}</td>
                    <td className="text-end">{totals.total.toLocaleString()}</td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
            <span className="small text-muted">
              {totalDistricts === 0
                ? "No districts"
                : `Showing ${rangeStart}–${rangeEnd} of ${totalDistricts} districts · click a district to filter the query list`}
            </span>
            {totalPages > 1 && (
              <Pagination size="sm" className="mb-0 flex-wrap">
                <Pagination.Prev
                  disabled={page === 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((p) => (
                  <Pagination.Item
                    key={p}
                    active={p === page}
                    disabled={loading}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={page === totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                />
              </Pagination>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DistrictQuerySummaryModal;
